import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { Stack } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import useLatestValue from "app/custom-hooks/useLatestValue";
import { AcknowledgeModal } from "app/pages/sales-intelligence/common-components/AcknowledgeModal/AcknowledgeModal";
import { renderErrorToast } from "app/pages/sales-intelligence/common-components/ErrorToast/ErrorToast";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { ScoringConfigType } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { collectAccountScopedScoringRuleIds } from "app/pages/sales-intelligence/domain/leads/utils/collectAccountScopedScoringRuleIds";
import { getTeamScoringSelection } from "app/pages/sales-intelligence/domain/teams/utils/getTeamScoringSelection";
import { useScoringFiltersBootstrap } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringFiltersBootstrap";
import useLeadScoringConfigQuery from "app/pages/sales-intelligence/modules/lead-scoring-agent/query/queries/useLeadScoringConfigQuery";
import { useTeamsTrackingService } from "app/pages/sales-intelligence/modules/teams/hooks/useTeamsTrackingService";
import { useUpdateTeamLeadScoringCriteriaMutation } from "app/pages/sales-intelligence/modules/teams/queries/useUpdateTeamLeadScoringCriteriaMutation";
import type { TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

import { TeamDetailTabFooter } from "../TeamDetailContainer/TeamDetailTabFooter";
import type { TeamDetailTabHandle } from "app/pages/sales-intelligence/pages/management/types/teamDetail.types";

import {
    areSelectionsEqual,
    buildLeadScoringCriteriaRules,
    omitRules,
    removeRule,
    toggleRule,
} from "./teamScoringSelection";
import { TeamScoringBoard } from "./TeamScoringBoard";

export type TeamScoringContainerHandle = TeamDetailTabHandle;

type Props = {
    team: TeamListItem;
};

export const TeamScoringContainer = forwardRef<TeamScoringContainerHandle, Props>(({ team }, ref) => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const tracking = useTeamsTrackingService();

    const { filtersList, isBootstrapping } = useScoringFiltersBootstrap();

    const teamName = team.name ?? "unnamed";

    const { data: accountConfig } = useLeadScoringConfigQuery(ScoringConfigType.Account);

    // Account-scoped rules are not attached to any team — exclude them from selection and the saved payload.
    const accountScopedRuleIds = useMemo(
        () => collectAccountScopedScoringRuleIds(accountConfig?.rules),
        [accountConfig?.rules],
    );

    const serverSelection = useMemo(
        () => omitRules(getTeamScoringSelection(team.leadScoringCriteria), accountScopedRuleIds),
        [team.leadScoringCriteria, accountScopedRuleIds],
    );

    const [savedSelection, setSavedSelection] = useState(serverSelection);
    const [draftSelection, setDraftSelection] = useState(serverSelection);
    const [hasTouched, setHasTouched] = useState(false);
    const [isSavedModalOpen, setIsSavedModalOpen] = useState(false);

    const draftSelectionRef = useLatestValue(draftSelection);
    const savedSelectionRef = useLatestValue(savedSelection);
    const hasTouchedRef = useLatestValue(hasTouched);

    const draftRuleIds = useMemo(() => new Set(draftSelection.keys()), [draftSelection]);
    const wizardDefaultTeamIds = useMemo(() => [team.teamId], [team.teamId]);

    useEffect(() => {
        setSavedSelection(serverSelection);

        if (!hasTouchedRef.current) {
            setDraftSelection(serverSelection);
        }
    }, [serverSelection]);

    const handleToggleRule = useCallback((ruleId: string, priority: LeadPriorityScore) => {
        setDraftSelection((current) => toggleRule(current, ruleId, priority));
        setHasTouched(true);
    }, []);

    // A rule deleted from the scoring config is gone for every team, so drop it from both sides of
    // the comparison — otherwise it would read as an unsaved change the user cannot undo.
    const handleRuleDeleted = useCallback((ruleId: string) => {
        setDraftSelection((current) => removeRule(current, ruleId));
        setSavedSelection((current) => removeRule(current, ruleId));
    }, []);

    const { mutateAsync: updateLeadScoringCriteria, isPending: isSaving } = useUpdateTeamLeadScoringCriteriaMutation();

    const handleSave = useCallback(() => {
        void (async () => {
            // A leftover account-scoped rule in the draft must not be saved back onto the team.
            const selection = omitRules(draftSelectionRef.current, accountScopedRuleIds);

            try {
                await updateLeadScoringCriteria({
                    teamId: team.teamId,
                    rules: buildLeadScoringCriteriaRules(selection),
                });
                tracking.trackScoringSaved(teamName, selection.size);
                setSavedSelection(selection);
                setDraftSelection(selection);
                setHasTouched(false);
                setIsSavedModalOpen(true);
            } catch {
                dispatch(
                    showErrorToast(
                        renderErrorToast({
                            content: translate("si.common.toast.error"),
                            minWidth: "auto",
                        }),
                    ),
                );
            }
        })();
    }, [
        draftSelectionRef,
        accountScopedRuleIds,
        updateLeadScoringCriteria,
        team.teamId,
        tracking,
        teamName,
        dispatch,
        translate,
    ]);

    const handleReset = useCallback(() => {
        setDraftSelection(savedSelectionRef.current);
        setHasTouched(false);
        tracking.trackScoringReset(teamName);
    }, [savedSelectionRef, tracking, teamName]);

    const handleCloseSavedModal = useCallback(() => setIsSavedModalOpen(false), []);

    useImperativeHandle(
        ref,
        () => ({
            hasUnsavedChanges: () => !areSelectionsEqual(draftSelectionRef.current, savedSelectionRef.current),
            discardChanges: () => {
                setDraftSelection(savedSelectionRef.current);
                setHasTouched(false);
            },
        }),
        [draftSelectionRef, savedSelectionRef],
    );

    const isActionDisabled = !hasTouched || isSaving;

    return (
        <>
            <Stack
                width="100%"
                flex="1"
                minHeight="0"
                data-automation="team-scoring"
            >
                <Stack
                    flex="1"
                    minHeight="0"
                    overflow="auto"
                    padding="16px 24px"
                    data-automation="team-scoring-board-area"
                >
                    <TeamScoringBoard
                        filtersList={filtersList}
                        isBootstrapping={isBootstrapping}
                        selectedRuleIds={draftRuleIds}
                        defaultTeamIds={wizardDefaultTeamIds}
                        onToggleRule={handleToggleRule}
                        onRuleDeleted={handleRuleDeleted}
                    />
                </Stack>

                <TeamDetailTabFooter
                    onSave={handleSave}
                    onReset={handleReset}
                    isSaveDisabled={isActionDisabled}
                    isResetDisabled={isActionDisabled}
                    isSaving={isSaving}
                    saveLabelKey="si.common.button.save"
                    resetLabelKey="si.common.button.cancel"
                    dataAutomationPrefix="team-scoring"
                />
            </Stack>

            <AcknowledgeModal
                isOpen={isSavedModalOpen}
                title={translate("si.management.teams.detail.scoring.saved_modal.title")}
                description={translate("si.management.teams.detail.scoring.saved_modal.description")}
                dataAutomation="team-scoring-saved-modal"
                onConfirm={handleCloseSavedModal}
            />
        </>
    );
});

TeamScoringContainer.displayName = "TeamScoringContainer";
